import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { SlidePanel } from "@/components/SlidePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Edit, Trash2, Scroll, Search, Copy, Sword, StickyNote, Users, Lock, Wand2 } from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import AICreateMode from "@/components/AICreateMode";
import { FolderRail, FolderToolbar, FolderCard, partitionCurrentLevel, useFolderCrud } from "@/components/FolderGrid";
import { CampaignBreadcrumbs } from "@/components/CampaignBreadcrumbs";
import EntityContextMenu from "@/components/EntityContextMenu";
import { FolderColorStrip } from "@/components/FolderColorStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LORE_CATEGORIES = ["general", "history", "legend", "artifact", "event", "prophecy", "organization", "secret"];

const CROSS_SAVE_OPTIONS = {
  artifact: { label: "Also save as Item", desc: "Creates a copy in Items as a Legendary Artifact", icon: Sword },
  legend: { label: "Also save as DM Note", desc: "Creates a Quest Hook note for your campaign", icon: StickyNote },
  prophecy: { label: "Also save as DM Note", desc: "Creates a Quest Hook note for your campaign", icon: StickyNote },
  event: { label: "Also save as Session Entry", desc: "Adds this as a note in a new Session Log", icon: StickyNote },
  organization: { label: "Also save as NPC", desc: "Creates an NPC entry representing this organisation", icon: Users },
  secret: { label: "Also save as DM Note", desc: "Creates a private DM note for reference", icon: Lock },
};

const LorePage = () => {
  const { campaignId } = useParams();
  const { user } = useAuth();
  const isCommoner = (user?.tier || "commoner") === "commoner";
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loreEntries, setLoreEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createMode, setCreateMode] = useState("manual");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [alsoCreateCopy, setAlsoCreateCopy] = useState(false);
  const [selectedLore, setSelectedLore] = useState(null);
  const [folders, setFolders] = useState([]);
  const folderCrud = useFolderCrud({ entityType: "lore", onRefresh: () => fetchData() });
  useEffect(() => { setFolders(folderCrud.folders); }, [folderCrud.folders]);
  const [formData, setFormData] = useState({
    title: "", content: "", category: "general", folder_id: ""
  });

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campaignRes, loreRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/lore`, { withCredentials: true })
      ]);
      setCampaign(campaignRes.data);
      setLoreEntries(loreRes.data);
    } catch (error) {
      toast.error("Failed to load lore");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        campaign_id: campaignId,
        folder_id: formData.folder_id === "none" || !formData.folder_id ? null : formData.folder_id,
      };
      
      if (editing) {
        await axios.put(`${API}/lore/${editing.id}`, payload, { withCredentials: true });
        toast.success("Lore updated");
      } else {
        await axios.post(`${API}/lore`, payload, { withCredentials: true });
        toast.success("Lore created");
      }

      // Cross-save based on category
      if (alsoCreateCopy && !editing && CROSS_SAVE_OPTIONS[formData.category]) {
        try {
          const cat = formData.category;
          if (cat === "artifact") {
            await axios.post(`${API}/items`, {
              campaign_id: campaignId, name: formData.title,
              description: formData.content, item_type: "artifact", rarity: "legendary",
            }, { withCredentials: true });
            toast.success(`Also saved "${formData.title}" to Items as an Artifact`);
          } else if (cat === "organization") {
            await axios.post(`${API}/npcs`, {
              campaign_id: campaignId, name: formData.title,
              description: formData.content, profession: "Organization",
              personality: `Organisation described in lore: ${formData.title}`,
            }, { withCredentials: true });
            toast.success(`Also saved "${formData.title}" to NPCs`);
          } else if (cat === "event") {
            const sessionRes = await axios.post(`${API}/sessions`, {
              campaign_id: campaignId, title: `Event: ${formData.title}`,
            }, { withCredentials: true });
            await axios.post(`${API}/sessions/${sessionRes.data.id}/entries`, {
              type: "note", content: formData.content || formData.title,
            }, { withCredentials: true });
            toast.success(`Also created Session Log: "Event: ${formData.title}"`);
          } else {
            // legend, prophecy, secret → DM Note
            await axios.post(`${API}/player-notes`, {
              campaign_id: campaignId,
              title: `${cat === "secret" ? "[SECRET] " : "Quest Hook: "}${formData.title}`,
              content: formData.content,
            }, { withCredentials: true });
            toast.success(`Also saved "${formData.title}" to Notes`);
          }
        } catch {
          toast.error("Lore saved, but failed to create the cross-save copy");
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save lore");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Lore", "Are you sure you want to delete this lore entry?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/lore/${id}`, { withCredentials: true });
      toast.success("Lore deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete lore");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setAlsoCreateCopy(false);
    setFormData({ title: "", content: "", category: "general", folder_id: folderCrud.currentFolderId || "" });
  };

  const openEdit = (lore) => {
    setEditing(lore);
    setFormData({
      title: lore.title,
      content: lore.content || "",
      category: lore.category || "general",
      folder_id: lore.folder_id || ""
    });
    setDialogOpen(true);
  };

  const openCreate = () => {
    resetForm();
    setCreateMode("manual");
    setDialogOpen(true);
  };

  const filteredLoreAll = loreEntries.filter(l => {
    const matchesSearch = l.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || l.category === filterCategory;
    return matchesSearch && matchesCategory;
  });
  const { folders: levelFolders, items: filteredLore } = partitionCurrentLevel({
    folders, items: filteredLoreAll, currentFolderId: folderCrud.currentFolderId
  });

  const moveLoreToFolder = async (loreId, folderId) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/lore/${loreId}/folder`, { folder_id: folderId }, { withCredentials: true });
      setLoreEntries(prev => prev.map(l => l.id === loreId ? { ...l, folder_id: folderId } : l));
      toast.success(folderId ? "Moved to folder" : "Unfiled");
    } catch { toast.error("Move failed"); }
  };

  if (loading) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout campaignName={campaign?.name} hideBreadcrumbs>
      <CampaignBreadcrumbs campaignName={campaign?.name} />
      <div data-testid="lore-page" className="flex gap-4">
        <FolderRail folders={folders} items={loreEntries} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} allLabel="All Lore" className="w-44 flex-shrink-0 sticky top-4 self-start" testIdPrefix="lore" />

        <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Lore</h1>
            <p className="text-muted-foreground text-sm">Record world history, legends, and secrets</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={openCreate}
                data-testid="create-lore-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Lore
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {editing ? "Edit Lore" : "New Lore Entry"}
                </DialogTitle>
              </DialogHeader>
              {!editing && (
                <div className="flex gap-2 border-b border-border pb-3 mb-1">
                  <Button variant={createMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("manual")} data-testid="mode-manual">
                    <Plus className="w-3 h-3 mr-1" /> Create Manually
                  </Button>
                  {!isCommoner && (
                    <Button variant={createMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("ai")} data-testid="mode-ai">
                      <Wand2 className="w-3 h-3 mr-1" /> Use AI
                    </Button>
                  )}
                  {isCommoner && (
                    <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled data-testid="mode-ai-locked">
                      <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                    </Button>
                  )}
                </div>
              )}
              {!editing && createMode === "ai" && (
                <AICreateMode entityType="lore" campaignId={campaignId} onGenerated={(data) => {
                  setFormData(prev => ({ ...prev, ...data }));
                  setCreateMode("manual");
                }} />
              )}
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" style={{ display: (!editing && createMode === "ai") ? "none" : undefined }}>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="The Fall of the Ancient Empire"
                    required
                    data-testid="lore-title-input"
                    className="bg-input border-border rounded-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Category</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="bg-input border-border rounded-sm" data-testid="lore-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {LORE_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Folder</label>
                  <Select value={formData.folder_id || "none"} onValueChange={v => setFormData({ ...formData, folder_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-input border-border rounded-sm" data-testid="lore-folder-select">
                      <SelectValue placeholder="Unfiled" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="none">Unfiled</SelectItem>
                      {folders.filter(f => !f.archived).sort((a, b) => a.name.localeCompare(b.name)).map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Content</label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="Write the lore entry..."
                  />
                </div>
                {formData.category !== "general" && formData.category !== "history" && CROSS_SAVE_OPTIONS[formData.category] && !editing && (
                  <label className="flex items-center gap-2 p-3 rounded-sm border border-border bg-muted/10 cursor-pointer hover:bg-muted/20 transition-colors" data-testid="cross-save-checkbox">
                    <input
                      type="checkbox"
                      checked={alsoCreateCopy}
                      onChange={(e) => setAlsoCreateCopy(e.target.checked)}
                      className="rounded border-border accent-primary"
                    />
                    <div>
                      <span className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        {(() => { const Ico = CROSS_SAVE_OPTIONS[formData.category].icon; return <Ico className="w-3.5 h-3.5" />; })()}
                        {CROSS_SAVE_OPTIONS[formData.category].label}
                      </span>
                      <span className="text-xs text-muted-foreground block mt-0.5">{CROSS_SAVE_OPTIONS[formData.category].desc}</span>
                    </div>
                  </label>
                )}
                <Button 
                  type="submit"
                  data-testid="save-lore-button"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                >
                  {editing ? "Save Changes" : "Create Lore"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search lore..."
              className="pl-10 bg-input border-border rounded-sm"
              data-testid="search-lore"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px] bg-input border-border rounded-sm" data-testid="filter-lore-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="all">All Categories</SelectItem>
              {LORE_CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Folder toolbar + cards */}
        <FolderToolbar folders={folders} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} onNewFolder={folderCrud.createHere} allLabel="All Lore" className="mb-3" />
        {levelFolders.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 mb-4" data-testid="lore-folder-grid">
            {levelFolders.map(f => (
              <FolderCard key={f.id} folder={f}
                count={loreEntries.filter(l => l.folder_id === f.id).length + folders.filter(x => x.parent_id === f.id && !x.archived).length}
                onOpen={() => folderCrud.setCurrentFolderId(f.id)}
                onRename={() => folderCrud.renameFolder(f)}
                onDelete={() => folderCrud.deleteFolder(f)}
                onArchive={() => folderCrud.toggleArchive(f)}
                onColor={(c) => folderCrud.setFolderColor(f, c)}
                onDropItem={folderCrud.moveItem} />
            ))}
          </div>
        )}

        {loreEntries.length === 0 ? (
          <div className="empty-state">
            <Scroll className="empty-state-icon text-muted-foreground" />
            <h3 className="text-xl font-serif text-foreground mb-2">No lore yet</h3>
            <p className="text-muted-foreground mb-6">Begin documenting your world's history</p>
            <Button 
              onClick={openCreate}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Lore
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            {filteredLore.map((lore) => (
              <EntityContextMenu
                key={lore.id}
                onOpen={() => setSelectedLore(lore)}
                onRename={() => openEdit(lore)}
                onDelete={() => handleDelete(lore.id)}
                folders={folders}
                currentFolderId={lore.folder_id}
                onMoveFolder={(fid) => moveLoreToFolder(lore.id, fid)}
                pinPath="lore"
                pinLabel={`Lore: ${lore.title}`}
                entityName={lore.title}
              >
              <Card
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("entityId", lore.id);
                  e.dataTransfer.setData("application/x-natone-entity", JSON.stringify({ entityType: "lore", entityId: lore.id, entityIds: [lore.id] }));
                  e.dataTransfer.effectAllowed = "move";
                }}
                className="bg-card border-border rounded-sm cursor-pointer entity-card relative"
                onClick={() => setSelectedLore(lore)}
                data-testid={`lore-card-${lore.id}`}
              >
                <FolderColorStrip entityId={lore.id} folderId={lore.folder_id} folders={folders} onFilter={folderCrud.setCurrentFolderId} testIdPrefix="lore" />
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                        <Scroll className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-serif text-lg text-foreground">{lore.title}</h3>
                        <p className="text-xs text-muted-foreground capitalize">{lore.category}</p>
                      </div>
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <VisibilityToggle
                        entityType="lore"
                        entityId={lore.id}
                        currentVisibility={lore.visibility || "dm_only"}
                        onUpdate={(v) => setLoreEntries(prev => prev.map(l => l.id === lore.id ? {...l, visibility: v} : l))}
                      />
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(lore)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(lore.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </EntityContextMenu>
            ))}
          </div>
        )}

        {/* Lore Detail SlidePanel */}
        <SlidePanel open={!!selectedLore} onClose={() => setSelectedLore(null)} title={selectedLore?.title || "Lore"}
          items={filteredLore} currentIndex={selectedLore ? filteredLore.findIndex(l => l.id === selectedLore.id) : -1}
          onNavigate={(i) => setSelectedLore(filteredLore[i])}
        >
          {selectedLore && (
            <div data-testid="lore-detail-panel">
              <div className="flex items-center gap-2 mb-4">
                <Scroll className="w-5 h-5 text-primary" />
                <span className="text-sm text-muted-foreground capitalize bg-primary/10 border border-primary/20 text-primary px-2 py-0.5 rounded-sm">{selectedLore.category}</span>
              </div>

              {selectedLore.content && (
                <div className="mb-4">
                  <div className="text-sm text-foreground/80 prose-sm">
                    <RichTextDisplay content={selectedLore.content} />
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t border-border">
                <Button variant="outline" size="sm" className="flex-1 rounded-sm" onClick={() => { setSelectedLore(null); openEdit(selectedLore); }} data-testid="lore-detail-edit">
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive rounded-sm" onClick={() => handleDelete(selectedLore.id)} data-testid="lore-detail-delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </SlidePanel>
        </div>
      </div>
    </Layout>
  );
};

export default LorePage;
